// The sixteen scoring cards plus the per-season extras (coins, monsters).
// Every evaluator is written against its card's printed text (quoted in
// cards.js, faces in public/cards/) and the general rules on p9:
// adjacency is orthogonal, a cluster is connected same-terrain (any size),
// mountains and monsters count as filled, ruins count as filled only when
// drawn on.

import {
  SIZE, MOUNTAINS, RUINS, isFilled, isRuin, neighbours, clusters,
} from './board';

const edge = (r, c) => r === 0 || c === 0 || r === SIZE - 1 || c === SIZE - 1;
const count = (grid, pred) => {
  let n = 0;
  for (let r = 0; r < SIZE; r += 1) {
    for (let c = 0; c < SIZE; c += 1) if (pred(r, c, grid[r][c])) n += 1;
  }
  return n;
};
const adjacentTo = (grid, r, c, ch) => neighbours(r, c).some(([rr, cc]) => grid[rr][cc] === ch);
const surrounded = (grid, r, c) => neighbours(r, c).every(([rr, cc]) => isFilled(grid, rr, cc));

const EVALUATORS = {
  // -- forests ---------------------------------------------------------------
  'sentinel-wood': (grid) => count(grid, (r, c, ch) => ch === 'f' && edge(r, c)),

  greenbough: (grid) => {
    let stars = 0;
    for (let i = 0; i < SIZE; i += 1) {
      if (grid[i].includes('f')) stars += 1; // row
      if (grid.some((row) => row[i] === 'f')) stars += 1; // column
    }
    return stars;
  },

  treetower: (grid) => count(grid, (r, c, ch) => ch === 'f' && surrounded(grid, r, c)),

  'stoneside-forest': (grid) => {
    // A mountain scores if some forest cluster it touches also touches
    // another mountain.
    const forestClusters = clusters(grid, 'f');
    const touching = forestClusters.map((cluster) => {
      const mountains = new Set();
      cluster.forEach(([r, c]) => {
        neighbours(r, c).forEach(([rr, cc]) => {
          if (grid[rr][cc] === 'M') mountains.add(`${rr},${cc}`);
        });
      });
      return mountains;
    });
    let stars = 0;
    MOUNTAINS.forEach(([r, c]) => {
      const id = `${r},${c}`;
      const connected = touching.some((set) => set.has(id) && set.size >= 2);
      if (connected) stars += 3;
    });
    return stars;
  },

  // -- farm and sea ----------------------------------------------------------
  'canal-lake': (grid) => count(grid, (r, c, ch) => (ch === 'w' && adjacentTo(grid, r, c, 'a'))
    || (ch === 'a' && adjacentTo(grid, r, c, 'w'))),

  'mages-valley': (grid) => count(grid, (r, c, ch) => ch === 'w' && adjacentTo(grid, r, c, 'M')) * 2
    + count(grid, (r, c, ch) => ch === 'a' && adjacentTo(grid, r, c, 'M')),

  'golden-granary': (grid) => {
    const nextToRuin = (r, c) => neighbours(r, c).some(([rr, cc]) => isRuin(rr, cc));
    return count(grid, (r, c, ch) => ch === 'w' && nextToRuin(r, c))
      + count(grid, (r, c, ch) => ch === 'a' && isRuin(r, c)) * 3;
  },

  'shoreside-expanse': (grid) => {
    const scoreClusters = (own, enemy) => clusters(grid, own)
      .filter((cluster) => !cluster.some(([r, c]) => edge(r, c)
        || neighbours(r, c).some(([rr, cc]) => grid[rr][cc] === enemy)))
      .length * 3;
    return scoreClusters('a', 'w') + scoreClusters('w', 'a');
  },

  // -- villages --------------------------------------------------------------
  wildholds: (grid) => clusters(grid, 'v').filter((c) => c.length >= 6).length * 8,

  'great-city': (grid) => {
    const eligible = clusters(grid, 'v').filter((cluster) => !cluster
      .some(([r, c]) => adjacentTo(grid, r, c, 'M')));
    return eligible.reduce((best, c) => Math.max(best, c.length), 0);
  },

  'greengold-plains': (grid) => clusters(grid, 'v').filter((cluster) => {
    const kinds = new Set();
    cluster.forEach(([r, c]) => {
      neighbours(r, c).forEach(([rr, cc]) => {
        const ch = grid[rr][cc];
        if (ch !== '.' && ch !== 'v') kinds.add(ch);
      });
    });
    return kinds.size >= 3;
  }).length * 3,

  shieldgate: (grid) => {
    const sizes = clusters(grid, 'v').map((c) => c.length).sort((a, b) => b - a);
    return (sizes[1] || 0) * 2;
  },

  // -- spatial ---------------------------------------------------------------
  borderlands: (grid) => {
    let complete = 0;
    for (let i = 0; i < SIZE; i += 1) {
      if (!grid[i].includes('.')) complete += 1;
      if (grid.every((row) => row[i] !== '.')) complete += 1;
    }
    return complete * 6;
  },

  'lost-barony': (grid) => {
    // Largest filled square, the classic dynamic programme: side[r][c] is the
    // biggest filled square whose bottom-right corner is (r, c).
    let best = 0;
    const side = Array.from({ length: SIZE }, () => new Array(SIZE).fill(0));
    for (let r = 0; r < SIZE; r += 1) {
      for (let c = 0; c < SIZE; c += 1) {
        if (!isFilled(grid, r, c)) continue; // eslint-disable-line no-continue
        side[r][c] = (r === 0 || c === 0) ? 1
          : Math.min(side[r - 1][c], side[r][c - 1], side[r - 1][c - 1]) + 1;
        best = Math.max(best, side[r][c]);
      }
    }
    return best * 3;
  },

  'broken-road': (grid) => {
    // Diagonals running down-right from the left edge to the bottom edge.
    let complete = 0;
    for (let r = 0; r < SIZE; r += 1) {
      let full = true;
      for (let i = 0; r + i < SIZE; i += 1) {
        if (!isFilled(grid, r + i, i)) { full = false; break; }
      }
      if (full) complete += 1;
    }
    return complete * 3;
  },

  cauldrons: (grid) => count(
    grid,
    (r, c, ch) => ch === '.' && surrounded(grid, r, c),
  ),
};

export const scoreEdict = (key, grid) => {
  const fn = EVALUATORS[key];
  if (!fn) throw new Error(`Unknown scoring card: ${key}`);
  return fn(grid);
};

// "loses one reputation star for each empty space adjacent to a monster
// space ... only loses one reputation star for that space" (rules p8).
export const monsterPenalty = (grid) => count(
  grid,
  (r, c, ch) => ch === '.' && adjacentTo(grid, r, c, 'm'),
);

export { RUINS };
